import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowUpTrayIcon, PencilSquareIcon, PlusCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, ConfirmModal, RowAction, Table } from "../ui";
import { errorMessageOf, scholarshipsApi } from "../../api/scholarships";
import { countryName, formatDuration, hasDestination, staffScholarshipKey, useScholarshipChange } from "./model";
import { canEditSetUp } from "./access";
import ReasonField, { reasonErrorOf, withReason } from "./ReasonField";
import ProgrammeDialog, { createProgramme } from "./ProgrammeDialog";
import ProgrammeImportDialog from "./ProgrammeImportDialog";

const MAX_PROGRAMMES = 50;

/**
 * Journey step 3: the programmes this scholarship offers. Each programme belongs to this
 * scholarship; adding, editing and removing one saves straight away. Only while the set-up is
 * open; once published, the reason is asked before any change.
 */
export default function ProgrammesTab({ scholarship }) {
  const queryClient = useQueryClient();
  const change = useScholarshipChange(scholarship.id);
  const [dialog, setDialog] = useState(null); // { programme } to edit, or {} to add
  const [removing, setRemoving] = useState(null);
  const [importing, setImporting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(null);

  const editable = canEditSetUp(scholarship);
  const programmes = scholarship.programmes ?? [];

  // Asked before a change starts, so a programme is never created without joining the list
  const withValidReason = (action) => () => {
    const error = reasonErrorOf(scholarship, reason);
    setReasonError(error);
    if (!error) action();
  };

  // The offered list is replaced as a whole
  const saveList = async (programmeIds) => {
    await change((etag) => scholarshipsApi.put(`admin/scholarships/${scholarship.id}/programmes`, withReason(scholarship, { programmeIds }, reason), { ifMatch: etag }));
    setReason("");
  };

  const onSaved = async (saved) => {
    // Edited: the list itself is unchanged, only reload it to show the new details
    if (programmes.some((p) => p.id === saved.id)) return queryClient.invalidateQueries({ queryKey: staffScholarshipKey(scholarship.id) });
    await saveList([...programmes.map((p) => p.id), saved.id]);
  };

  // Imported programmes join the list in one save; those already offered are not added twice
  const onImported = (ids) => {
    const current = programmes.map((p) => p.id);
    const added = [...new Set(ids)].filter((id) => !current.includes(id));
    return added.length ? saveList([...current, ...added]) : null;
  };

  const remove = async () => {
    setBusy(true);
    try {
      await saveList(programmes.filter((p) => p.id !== removing.id).map((p) => p.id));
      toast.success(`${removing.name} removed`);
      setRemoving(null);
    } catch (error) {
      console.error(`Failed to remove programme ${removing.id} from scholarship ${scholarship.id}:`, error);
      toast.error(errorMessageOf(error, "Failed to remove the programme. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  // Abroad loan programmes are study levels: the student names the university and the country
  const places = hasDestination(scholarship.type);
  const columns = [
    { key: "#", label: "#", render: (_, i) => i + 1 },
    { key: "name", label: "Programme", render: (p) => <span className="font-medium text-slate-800">{p.name}</span> },
    ...(places
      ? [
          { key: "institutionName", label: "University" },
          { key: "country", label: "Country", accessor: (p) => countryName(p.countryCode) },
        ]
      : []),
    { key: "level", label: "Level", accessor: (p) => p.level?.name ?? "—" },
    { key: "duration", label: "Duration", accessor: (p) => formatDuration(p.durationMonths), sortValue: (p) => p.durationMonths },
    { key: "documents", label: "Documents", accessor: (p) => (p.requirements ?? []).length },
    ...(editable
      ? [
          {
            key: "actions",
            label: "",
            exportable: false,
            render: (p) => (
              <div className="flex justify-end gap-1">
                <RowAction Icon={PencilSquareIcon} label="Edit" onClick={() => setDialog({ programme: p })} disabled={busy} />
                <RowAction Icon={TrashIcon} label="Remove" onClick={withValidReason(() => setRemoving(p))} disabled={busy} />
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      {editable && (
        <ReasonField
          scholarship={scholarship}
          value={reason}
          onChange={(value) => {
            setReason(value);
            setReasonError(null);
          }}
          error={reasonError}
          disabled={busy}
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-700">Programmes ({programmes.length})</h3>
        {editable && programmes.length < MAX_PROGRAMMES && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" icon={<ArrowUpTrayIcon className="h-4 w-4" />} onClick={withValidReason(() => setImporting(true))}>
              Import from Excel
            </Button>
            <Button icon={<PlusCircleIcon className="h-4 w-4" />} onClick={withValidReason(() => setDialog({}))}>
              Add programme
            </Button>
          </div>
        )}
      </div>
      <Table columns={columns} data={programmes} searchable={false} exportable={false} emptyTitle={editable ? "No programmes yet. Add the first one." : "No programmes"} />

      {dialog && (
        <ProgrammeDialog
          key={dialog.programme?.id ?? "new"}
          open
          programme={dialog.programme}
          countryCode={scholarship.destinationCountryCode}
          withPlace={places}
          onClose={() => setDialog(null)}
          onSaved={onSaved}
        />
      )}
      {importing && (
        <ProgrammeImportDialog
          open
          place={places ? "required" : "none"}
          limit={MAX_PROGRAMMES - programmes.length}
          saveRow={createProgramme}
          onImported={onImported}
          onClose={() => setImporting(false)}
        />
      )}
      <ConfirmModal
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        loading={busy}
        destructive
        title={`Remove ${removing?.name}?`}
        description={
          removing?.requirements?.length
            ? `Its ${removing.requirements.length} required document${removing.requirements.length === 1 ? "" : "s"} will be deleted too.`
            : "Students will no longer be able to apply for it."
        }
        confirmLabel="Remove"
      />
    </div>
  );
}
